/* eslint-disable no-continue */
// Vendor libraries
import { Subject } from 'rxjs';
import Highcharts, { positionChartElements } from 'ChartApp/ts/features/scatter-plot/highcharts';
// Utils / Constants
import { FIGURE, getFormatter } from 'NewAngular/utils/number_formatter';
import { isMobile } from 'NewAngular/utils/media';
import { DEFAULT_SCATTER_PLOT_CUSTOM, SCATTER_PLOT_MARKER_OPACITY_HEX_STR, YC_DEFAULT_LABEL_ALIGNMENT, YC_SHAPE_MAP_SCATTER_PLOT, YC_SIZE_MAP_SCATTER_PLOT, } from './constants';
import { MAX_POINTS_PER_SERIES, SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_INTERACTIVE_DEFAULT, SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_PRESENTATION_DEFAULT, SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_PRESENTATION_EFFICIENT_FRONTIER, SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_EFFICIENT_FRONTIER_DEFAULT, } from 'ChartApp/ts/features/scatter-plot/constants/scatter-plot-highcharts-plot-options.constants';
import { noXMetricData, noYMetricData } from 'ChartApp/ts/features/scatter-plot/constants/messages.constants';
import { ScatterPlotChartType, ScatterPlotSeriesType } from 'ChartApp/ts/enums/scatter-plot-common.enum';
import { ScatterPlotDataType, } from 'ChartApp/ts/interfaces/scatter-plot-service-responses.interface';
/**
 * YChartsScatterPlotService
 *
 * A service that creates scatter plot highcharts options that can be used to create a Highchart.
 * Note this does not actually create a Highchart.
 *
 * Usage Example:
 *  const ychartsScatterPotService = new YChartsScatterPlotService();
 *  const options = ychartsScatterPlotService.setProcessedChartOptions(chartInfo, chartElemId);
 *  const chart = this.ychartsScatterPlotService.createChartFromOptions(
 *      chartElemId,
 *      options,
 *      scatterPlotInfo.params.axisExtremes
 *  );
 */
export class YChartsScatterPlotService {
    /**
     * @param hideDataLabels whether to override the chart options and always hide data labels unless hovering over a point.
     * @param repositionChartElements whether to reposition chart elements based on the chart spacing. This is not necessary in the dashboard scatter plot module
     *                                So we set this property to false in when using this service to create a chart there.
     */
    constructor(hideDataLabels = false, repositionChartElements = true) {
        this.hideDataLabels = hideDataLabels;
        this.repositionChartElements = repositionChartElements;
        this.pointClick$ = YChartsScatterPlotService.pointClickSubject.asObservable();
        this.pointSelect$ = YChartsScatterPlotService.pointSelectSubject.asObservable();
    }
    /**
     * Public Methods
     */
    /**
     * Merge user selected chart options with default chart options.
     *
     * @param scatterPlotInfo - Response returned from the ScatterPlotDataView.
     * @param chartElemId - The id attribute of the html tag that wraps around the chart.
     * @param chartType - The chart type (interactive or presentation) to determine which highcharts plot options
     *  should be used.
     * @param scatterPlotChartOptions - An object describing style/formatting in the Highchart chart. This is used by
     *  the chart generator to format static images of various sizes.
     * @returns options that can be used to create a Highchart
     * @public
     */
    createChartOptions(scatterPlotInfo, chartElemId, chartType, scatterPlotChartOptions) {
        const { data: scatterPlotData, params: scatterPlotParams } = scatterPlotInfo;
        // Configure plot options so the chart is displayed in the correct html element
        let chartOptions = SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_INTERACTIVE_DEFAULT;
        if (chartType === ScatterPlotChartType.presentation) {
            chartOptions =
                scatterPlotData.data_type === ScatterPlotDataType.efficientFrontier
                    ? SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_PRESENTATION_EFFICIENT_FRONTIER
                    : SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_PRESENTATION_DEFAULT;
        }
        else if (scatterPlotData.data_type === ScatterPlotDataType.efficientFrontier) {
            chartOptions = SCATTER_PLOT_HIGHCHARTS_PLOT_OPTIONS_EFFICIENT_FRONTIER_DEFAULT;
        }
        if (scatterPlotChartOptions) {
            chartOptions = YChartsScatterPlotService.setFontSize(chartOptions, scatterPlotChartOptions);
            if ('xNumTicks' in scatterPlotChartOptions) {
                chartOptions = Highcharts.merge(chartOptions, {
                    xAxis: { tickAmount: scatterPlotChartOptions.xNumTicks },
                });
            }
        }
        // Configure plot options so the chart is displayed in the correct html element
        chartOptions = YChartsScatterPlotService.renderToChart(chartOptions, chartElemId);
        // If there is no data or if using a mobile device, disable crosshairs
        const dataExists = this.seriesDataExists(scatterPlotData.series);
        if (!dataExists || isMobile()) {
            chartOptions = YChartsScatterPlotService.disableCrosshairs(chartOptions);
        }
        // If there is data, configure chart options to display it
        if (dataExists) {
            // Get data formatters
            // https://api.highcharts.com/class-reference/Highcharts#.AxisLabelsFormatterCallbackFunction
            const xDataFormatter = getFormatter(scatterPlotData.x_axis_type);
            const yDataFormatter = getFormatter(scatterPlotData.y_axis_type);
            chartOptions = this.setUpDataSeries(chartOptions, scatterPlotData.series, xDataFormatter, yDataFormatter, scatterPlotData.label_placements, scatterPlotData.data_type, scatterPlotChartOptions?.dataLabel);
            chartOptions = this.setUpAxes(chartOptions, scatterPlotData.x_axis, xDataFormatter, scatterPlotData.y_axis, yDataFormatter, scatterPlotParams.plotLines);
        }
        return chartOptions;
    }
    createChartFromOptions(chartElemId, options) {
        const _chart = new Highcharts.Chart(chartElemId, options);
        if (this.repositionChartElements) {
            positionChartElements(_chart);
        }
        return YChartsScatterPlotService.addCustomChartProperties(_chart);
    }
    /**
     * Filters out series that have more than MAX_POINTS_PER_SERIES points
     * @param series - A list of scatter plot series returned from the backend.
     * @returns A list of scatter plot series that are under the max points threshold.
     * @private
     */
    filterSeriesWithTooManyPoints(series) {
        return series.filter((s) => s.data.length < MAX_POINTS_PER_SERIES);
    }
    /**
     * Returns whether or not there is any data in a list of scatter plot series.
     * @param series - A list of scatter plot series returned from the backend.
     * @returns A boolean, when true, means series data exists.
     * @public
     */
    seriesDataExists(series) {
        return series.some((s) => {
            return s.data.some((seriesData) => {
                // To create a scatter plot, there must be at least one point with data and dates for both x and y axes.
                // Otherwise, the plot would be empty and a no data message should display.
                // Here, we assume the backend will only return x, y data if their dates exist.
                return seriesData.x && seriesData.y;
            });
        });
    }
    /**
     * Attempts to return a helpful error message based on the series data.
     * @param series - A list of scatter plot series returned from the backend.
     * @returns A string if a specific helpful error message could be determined, or else the empty string.
     * @public
     */
    getSpecificErrorMessage(series) {
        let xMetricIsValid = true;
        let yMetricIsValid = true;
        for (let i = 0; i < series.length; i += 1) {
            const _series = series[i];
            const dataList = _series.data;
            for (let j = 0; j < dataList.length; j += 1) {
                const data = dataList[j];
                xMetricIsValid = xMetricIsValid && !!data.x;
                yMetricIsValid = yMetricIsValid && !!data.y;
                // If any series is invalid, stop checking and return an error message
                if (!(xMetricIsValid && yMetricIsValid)) {
                    break;
                }
            }
        }
        if (!xMetricIsValid)
            return noXMetricData;
        if (!yMetricIsValid)
            return noYMetricData;
        return '';
    }
    /**
     * Chart
     */
    /**
     * Converts ScatterPlotSeries[] returned from the backend into Highcharts.SeriesScatterOptions[],
     * sets the color, shape, and size of point markers and sets up the data labels and tooltips for each data point.
     *
     * @param chartOptions - The chart options to be modified.
     * @param scatterPlotSeries - A list of series of data points to be plotted on the chart.
     * @param xDataFormatter - A function that formats x values.
     * @param yDataFormatter - A function that formats y values.
     * @param labelPlacements - An object mapping unique point IDs to label placements.
     * @param dataType - Enum defining ScatterPlot types to add custom functionability.
     * @param scatterPlotDataLabelOptions - An object describing style/formatting in the data label. This is used by
     *  the chart generator to format static images of various sizes.
     * @returns A list of series of data points in a format Highcharts expects.
     * @private
     * */
    setUpDataSeries(chartOptions, scatterPlotSeries, xDataFormatter, yDataFormatter, labelPlacements, dataType, scatterPlotDataLabelOptions) {
        let seriesScatterOptions = [];
        YChartsScatterPlotService.MAX_DATA_LABEL_Z_INDEX = YChartsScatterPlotService.numDataPoints(scatterPlotSeries);
        let zIndex = YChartsScatterPlotService.MAX_DATA_LABEL_Z_INDEX;
        // This object will be updated with the min / max data points in our actual dataset.
        const axisExtremes = {
            minX: Number.MAX_VALUE,
            maxX: Number.MIN_VALUE,
            minY: Number.MAX_VALUE,
            maxY: Number.MIN_VALUE,
        };
        // Convert each series
        for (let i = 0; i < scatterPlotSeries.length; i += 1) {
            const series = scatterPlotSeries[i];
            const seriesScatterData = [];
            // If any of the options are true, display the data label
            const displayDataLabel = (series.display_currency_code ||
                series.display_dates ||
                series.display_name ||
                series.display_ticker ||
                series.display_x_y_data) &&
                !this.hideDataLabels;
            // Convert each data point
            for (let j = 0; j < series.data.length; j += 1) {
                const data = series.data[j];
                /*
                 * The closer to the top of a user's list of selections, the more important we assume that
                 * security/security list to be.
                 * The first selected security/security list's data label is displayed at the very top,
                 * the last selected security/security list's data label is on the bottom.
                 */
                zIndex -= 1;
                // If any of these required properties are missing, don't add the point to the series data
                if (data.x === null || data.y === null) {
                    continue;
                }
                if (data.x < axisExtremes.minX)
                    axisExtremes.minX = data.x;
                if (data.x > axisExtremes.maxX)
                    axisExtremes.maxX = data.x;
                if (data.y < axisExtremes.minY)
                    axisExtremes.minY = data.y;
                if (data.y > axisExtremes.maxY)
                    axisExtremes.maxY = data.y;
                let parentSecurityListId = '';
                if (series.type === ScatterPlotSeriesType.securityList) {
                    parentSecurityListId = series.id;
                }
                const uniquePointId = `${data.security_id}-${data.x_date}-${data.y_date}`;
                const fillColor = series.include_through_line
                    ? series.color
                    : `${series.color}${data?.opacity ?? SCATTER_PLOT_MARKER_OPACITY_HEX_STR}`;
                seriesScatterData.push({
                    custom: {
                        label: {
                            id: uniquePointId,
                            labelPlacement: labelPlacements[uniquePointId],
                            showCurrencyCode: series.display_currency_code,
                            showDates: series.display_dates,
                            showName: series.display_name,
                            showTicker: series.display_ticker,
                            showXYData: series.display_x_y_data,
                        },
                        currencyCode: data.currency_code,
                        parentSecurityListId,
                        securityName: data.security_name,
                        xDate: data.x_date || '',
                        xFormatted: xDataFormatter(data.x),
                        yFormatted: yDataFormatter(data.y),
                        yDate: data.y_date || '',
                        frontierPoint: data.frontier_point,
                    },
                    dataLabels: {
                        enabled: displayDataLabel,
                        formatter: this.dataLabelFormatter(scatterPlotDataLabelOptions, series.series_label_location),
                        zIndex,
                        ...data.noHTMLStyles,
                    },
                    id: data.security_id,
                    marker: {
                        symbol: YC_SHAPE_MAP_SCATTER_PLOT[series.shape],
                        radius: YC_SIZE_MAP_SCATTER_PLOT[series.size],
                        /*
                         * The Highcharts.series.scatter.opacity option sets multiple series parts to the same opacity
                         * i.e. series parts: line, fill (area), and dataLabels.
                         * However, we want the marker and the dataLabels to have different opacity (except
                         * for series with a through line, since we don't want the through line to be visible
                         * through the series points), so we set the alpha channel in the hex color string
                         * to adjust the marker's opacity.
                         */
                        fillColor,
                        lineColor: series.color,
                        // Since opacity close to 0 makes shapes invisible
                        // display borders when opacity value is returned.
                        lineWidth: data?.opacity ? 1 : 0,
                        states: {
                            select: {
                                radius: YC_SIZE_MAP_SCATTER_PLOT.extra_large,
                                fillColor: series.selected_color ?? fillColor,
                                lineColor: series.line_color ?? series.color,
                                lineWidth: series.line_width ?? 1,
                            },
                        },
                    },
                    className: series.series_class,
                    name: data.security_name,
                    x: data.x,
                    y: data.y,
                });
            }
            if (series.include_through_line) {
                seriesScatterOptions.push({
                    type: 'line',
                    stickyTracking: false,
                    id: series.name,
                    tooltip: {
                        headerFormat: undefined,
                        // Only show the tooltip when data labels are not shown
                        pointFormatter: () => '',
                    },
                    color: series.color,
                    data: seriesScatterData.map(({ x, y }) => [x, y]),
                    marker: {
                        enabled: false,
                    },
                });
            }
            seriesScatterOptions.push({
                // Set a default color value to override
                // Otherwise Highcharts default colors (instead of the desired colors) are used
                color: '#000000',
                data: seriesScatterData,
                point: {
                    events: {
                        click: this.click,
                        select: this.select,
                    },
                },
                id: series.name,
                tooltip: {
                    headerFormat: undefined,
                    // Only show the tooltip when data labels are not shown
                    pointFormatter: displayDataLabel ? () => '' : this.pointFormatter,
                },
                // Show the tooltip only when the mouse is close to the data point
                stickyTracking: false,
                type: 'scatter',
                allowPointSelect: series.can_select_point,
            });
        }
        if (dataType === ScatterPlotDataType.scatterPlot) {
            seriesScatterOptions = seriesScatterOptions.concat(YChartsScatterPlotService.addInvisiblePoints(axisExtremes));
        }
        return Highcharts.merge(chartOptions, {
            series: seriesScatterOptions,
            tooltip: { enabled: true, snap: 10, useHTML: true },
        });
    }
    /**
     * Add a series of invisible points, whose x/y are several ticks greater/lesser than the max/min plotted points.
     * This is done because:
     *   - Highcharts does not allow us to pan past our data points. This means when panning, data points end up on the very
     *     edge of the chart. Adding invisible points that are plotted further out prevents this.
     *   - When the chart is first loaded, data points can end up on the very edge of the chart. Adding invisible points
     *     creates padding to prevent this.
     *
     * Source: https://www.highcharts.com/forum/viewtopic.php?t=37223
     */
    static addInvisiblePoints(axisExtremes) {
        // How much extra padding to add on each side of the chart.
        const PADDING_MULTIPLIER = 0.2;
        const { minX, maxX, minY, maxY } = axisExtremes;
        const minXInvisiblePoint = minX - (maxX - minX) * PADDING_MULTIPLIER;
        const maxXInvisiblePoint = maxX + (maxX - minX) * PADDING_MULTIPLIER;
        const minYInvisiblePoint = minY - (maxY - minY) * PADDING_MULTIPLIER;
        const maxYInvisiblePoint = maxY + (maxY - minY) * PADDING_MULTIPLIER;
        // We return the invisible points as 2 separate series. There is either a Highcharts bug or something wrong
        // with our configuration, where adding the invisible points as a single series prevents the zoom from working
        // properly (it becomes impossible to zoom to an area that doesn't contain an invisible point). We may face a
        // similar issue when we add a best fit line.
        return [
            {
                data: [
                    {
                        x: minXInvisiblePoint,
                        y: minYInvisiblePoint,
                        marker: { enabled: false },
                    },
                ],
                enableMouseTracking: false,
                id: 'invisibleSeries1',
                type: 'scatter',
            },
            {
                data: [
                    {
                        x: maxXInvisiblePoint,
                        y: maxYInvisiblePoint,
                        marker: { enabled: false },
                    },
                ],
                enableMouseTracking: false,
                id: 'invisibleSeries2',
                type: 'scatter',
            },
        ];
    }
    static setFontSize(plotOptions, scatterPlotChartOptions) {
        const { fontSize, dataLabel } = scatterPlotChartOptions;
        return Highcharts.merge(plotOptions, {
            chart: {
                style: {
                    fontSize: `${fontSize}px`,
                },
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        style: {
                            fontSize: `${dataLabel.fontSize}px`,
                        },
                    },
                },
            },
            xAxis: {
                labels: {
                    style: {
                        fontSize: `${fontSize}px`,
                    },
                },
                title: {
                    style: {
                        fontSize: `${fontSize}px`,
                    },
                },
            },
            yAxis: {
                labels: {
                    style: {
                        fontSize: `${fontSize}px`,
                    },
                },
                title: {
                    style: {
                        fontSize: `${fontSize}px`,
                    },
                },
            },
        });
    }
    /**
     * Update scatter plot options to render the chart to the div with a given html id.
     * @param chartOptions - The chart options to be modified.
     * @param chartElemId - The value of the html id attribute on the html element that wraps around the chart.
     * @returns The modified plot options.
     * @private
     */
    static renderToChart(chartOptions, chartElemId) {
        return Highcharts.merge(chartOptions, {
            chart: { renderTo: chartElemId },
        });
    }
    /**
     * Chart Utils
     */
    /**
     * Return the total number of data points (a count of the number of data points in all series).
     * @param series - A list of scatter plot series returned from the backend.
     * @returns The total number of data points
     * @private
     */
    static numDataPoints(series) {
        let num = 0;
        for (let i = 0; i < series.length; i += 1) {
            num += series[i].data.length;
        }
        return num;
    }
    /**
     * Update scatter plot options to disable the x and y crosshairs that follow the mouse.
     * @param chartOptions - The chart options to be modified.
     * @returns The modified plot options.
     * @private
     */
    static disableCrosshairs(chartOptions) {
        return Highcharts.merge(chartOptions, {
            xAxis: { crosshair: { width: 0 } },
            yAxis: { crosshair: { width: 0 } },
        });
    }
    /**
     * Set label formatting functions for the x and y axis.
     * @param chartOptions - The chart options to be modified.
     * @param xAxisTitle - The title of the x-axis.
     * @param xDataFormatter - A function that formats x-axis labels.
     * @param yAxisTitle - The title of the y-axis.
     * @param yDataFormatter - A function that formats y-axis lables.
     * @returns - The modified chart options.
     * @private
     */
    setUpAxes(chartOptions, xAxisTitle, xDataFormatter, yAxisTitle, yDataFormatter, plotlineOptions = []) {
        const xPlotLines = [];
        const yPlotLines = [];
        plotlineOptions.forEach(({ axis, value }) => {
            const axisLinesCollection = axis === 'x' ? xPlotLines : yPlotLines;
            axisLinesCollection.push({
                color: '#000000',
                width: 2,
                value,
            });
        });
        return Highcharts.merge(chartOptions, {
            xAxis: {
                labels: {
                    formatter: (contextObject) => {
                        return xDataFormatter(contextObject.value);
                    },
                },
                title: {
                    text: xAxisTitle,
                },
                plotLines: xPlotLines,
            },
            yAxis: {
                labels: {
                    formatter: (contextObject) => {
                        return yDataFormatter(contextObject.value);
                    },
                },
                title: {
                    text: yAxisTitle,
                },
                plotLines: yPlotLines,
            },
        });
    }
    /**
     * Events
     */
    /**
     * 1. Change the clicked data label's zIndex to be greater than all other data labels' zIndexes.
     *    This is used to make the clicked data label appear above other overlapping data labels.
     * 2. Emit a value in an Observable that will eventually open the customize modal for that security.
     * @returns void
     * @private
     */
    click(event) {
        const target = event.target;
        const label = target.closest('.highcharts-label');
        if (YChartsScatterPlotService.topDataLabel) {
            YChartsScatterPlotService.topDataLabel.style.zIndex = '1';
        }
        if (label) {
            label.style.zIndex = '2';
            YChartsScatterPlotService.topDataLabel = label;
        }
        const pointOptions = this.options;
        YChartsScatterPlotService.pointClickSubject.next({
            parentSecurityListId: pointOptions.custom.parentSecurityListId,
            securityId: pointOptions.id,
            securityName: pointOptions.custom.securityName,
            uniquePointId: pointOptions.custom.label.id,
        });
    }
    /**
     * 1. Emit the selected portfolio data.
     * @returns void
     * @private
     */
    select() {
        const { custom, id } = this.options;
        if ('graphic' in this) {
            this.graphic.toFront();
        }
        if (custom.frontierPoint) {
            YChartsScatterPlotService.pointSelectSubject.next({
                ...custom.frontierPoint,
                index: this.index,
                series: id,
            });
        }
    }
    /**
     * Data Label and Tooltip Utils
     *
     * Note: There are several static methods because they are called from a Highcharts callback function, which doesn't
     * automatically have access to the service class's scope.
     */
    /**
     * Create a string of html formatted info for data labels and tooltips.
     *
     * @param isDataLabel - Whether the label is a data label. If false, we assume the label is a tooltip.
     * @param point - A point object containing information to display about a data point.
     * @param alignment - An object describing how the label should be aligned relative to the data point.
     * @param scatterPlotDataLabelOptions - An object describing style/formatting in the data labels. This is used by
     *  the chart generator to format static images of various sizes.
     * @returns A string of html formatted info about a data point.
     * @private
     */
    static createLabel(isDataLabel, point, alignment, scatterPlotDataLabelOptions) {
        const pointOptions = point.options;
        const { name, id } = pointOptions;
        const { xFormatted, xDate, yFormatted, yDate, currencyCode, frontierPoint } = pointOptions.custom;
        let { id: labelId, showCurrencyCode, showDates, showName, showTicker, showXYData } = pointOptions.custom.label;
        // eslint-disable-next-line prettier/prettier
        if (!isDataLabel) {
            labelId = '';
            showCurrencyCode = false;
            showDates = true;
            showName = true;
            showTicker = false;
            showXYData = true;
        }
        // Set up the label title
        const labelType = isDataLabel ? 'datalabel' : 'tooltip';
        const alignX = alignment.x ? `align-${alignment.x}` : 'align-right';
        // Omitting the y alignment css class uses the default middle position
        const alignY = alignment.y ? `align-${alignment.y}` : '';
        // If there are data label options, use them to style the label
        let labelStyleStr = '';
        let titleStyleStr = '';
        let rowStyleStr = '';
        let colStyleStr = '';
        if (scatterPlotDataLabelOptions) {
            const { maxWidth, titleMaxWidth, rowLabelMaxWidth, dataColMaxWidth } = scatterPlotDataLabelOptions;
            labelStyleStr = `max-width:${maxWidth}px`;
            titleStyleStr = `max-width:${titleMaxWidth}px`;
            rowStyleStr = `max-width:${rowLabelMaxWidth}px`;
            colStyleStr = `max-width:${dataColMaxWidth}px`;
        }
        let label = `<div id="${labelId}"` +
            `class="scatter-${labelType} ${alignX} ${alignY} user-select-none max-width-130"` +
            `style="${labelStyleStr}">`;
        if (frontierPoint) {
            return this.createFrontierLabel(isDataLabel, point, alignment, frontierPoint, scatterPlotDataLabelOptions);
        }
        // Set up the label content
        const labelName = showName ? `${name}` : '';
        let ticker = showTicker ? `${id}` : '';
        ticker = showName && showTicker ? ` (${id})` : ticker;
        const currency = showCurrencyCode && currencyCode ? `<span style="font-weight: normal"> (${currencyCode})</span>` : '';
        label += `<div class="highcharts-tooltip-title" style="${titleStyleStr}">${labelName}${ticker}${currency}</div>`;
        // Set up the table of point data
        label += '<table class="highcharts-tooltip-table"><tbody>';
        let xRow = '';
        let yRow = '';
        if (showXYData || showDates) {
            xRow += `<tr><td class="td-ellipsis-content" style="${rowStyleStr}">X:</td>`;
            yRow += `<tr><td class="td-ellipsis-content" style="${rowStyleStr}">Y:</td>`;
        }
        if (showXYData) {
            xRow += `<td class="text-right td-ellipsis-content" style="${colStyleStr}">${xFormatted}</td>`;
            yRow += `<td class="text-right td-ellipsis-content" style="${colStyleStr}">${yFormatted}</td>`;
        }
        if (showDates) {
            xRow += `<td class="td-ellipsis-content" style="${colStyleStr}">${xDate}</td>`;
            yRow += `<td class="td-ellipsis-content"style="${colStyleStr}">${yDate}</td>`;
        }
        xRow += '</tr>';
        yRow += '</tr>';
        label += `${xRow + yRow}</tbody></table></div>`;
        return label;
    }
    /**
     * Create a string of html formatted info for data labels and tooltips specific for efficient frontier tooltips.
     *
     * @param isDataLabel - Whether the label is a data label. If false, we assume the label is a tooltip.
     * @param point - A point object containing information to display about a data point.
     * @param alignment - An object describing how the label should be aligned relative to the data point.
     * @param frontierPoint - An Efficient Frontier Portfolio.
     * @param scatterPlotDataLabelOptions - An object describing style/formatting in the data labels. This is used by
     *  the chart generator to format static images of various sizes.
     * @returns A string of html formatted info about a data point.
     * @private
     */
    static createFrontierLabel(isDataLabel, point, alignment, frontierPoint, scatterPlotDataLabelOptions) {
        const formatterDecimal = getFormatter(FIGURE);
        const pointOptions = point.options;
        const { xFormatted, yFormatted } = pointOptions.custom;
        let { id: labelId } = pointOptions.custom.label;
        if (!isDataLabel) {
            labelId = '';
        }
        // Set up the label title
        const labelType = isDataLabel ? 'datalabel' : 'tooltip';
        const alignX = alignment.x ? `align-${alignment.x}` : 'align-right';
        // Omitting the y alignment css class uses the default middle position
        const alignY = alignment.y ? `align-${alignment.y}` : '';
        // If there are data label options, use them to style the label
        let labelStyleStr = '';
        let rowStyleStr = '';
        let colStyleStr = '';
        if (scatterPlotDataLabelOptions) {
            const { maxWidth, rowLabelMaxWidth, dataColMaxWidth } = scatterPlotDataLabelOptions;
            labelStyleStr = `max-width:${maxWidth}px`;
            rowStyleStr = `max-width:${rowLabelMaxWidth}px`;
            colStyleStr = `max-width:${dataColMaxWidth}px`;
        }
        let label = `<div id="${labelId}"` +
            `class="scatter-${labelType} ${alignX} ${alignY} user-select-none max-width-130"` +
            `style="${labelStyleStr}">`;
        label += '<table class="highcharts-tooltip-table"><tbody>';
        const buildRow = (title, value) => `<tr>
                <td class="td-ellipsis-content" style="${rowStyleStr}">${title}:</td>
                <td class="text-right td-ellipsis-content" style="${colStyleStr}"><b>${value}</b></td>
            </tr>`;
        const expReturnRow = buildRow('Expected Return', yFormatted);
        const StandardDevRow = buildRow('Standard Dev', xFormatted);
        const sharpeRatioRow = buildRow('Sharpe Ratio', formatterDecimal(frontierPoint.sharpe_ratio));
        label += `${expReturnRow + StandardDevRow + sharpeRatioRow}</tbody></table></div>`;
        return label;
    }
    /**
     * Get the html formatted string to display as a label.
     *
     * @param point - An object containing info to display about the data point.
     * @param isDataLabel - Whether the label is a data label. If not, it is a tooltip.
     * @param scatterPlotDataLabelOptions - An object describing style/formatting in a data label. This is used by
     *  the chart generator to format static images of various sizes.
     * @param seriesLabelLocation - A string describing label positioning of the series.
     */
    static formatter(point, isDataLabel, scatterPlotDataLabelOptions, seriesLabelLocation) {
        const alignment = seriesLabelLocation
            ? YChartsScatterPlotService.locationNameToLabelAligment(seriesLabelLocation)
            : YChartsScatterPlotService.getLabelAlignment(point.series.chart.xAxis[0], point.series.chart.yAxis[0], point);
        return YChartsScatterPlotService.createLabel(isDataLabel, point, alignment, scatterPlotDataLabelOptions);
    }
    /**
     * Get x and y axis extremes in pixel units.
     *
     * @param axis - A Highcharts axis object.
     * @returns An object of min and max values for each axis in pixel units.
     */
    static getExtremesPixels(axis) {
        const extremes = axis.getExtremes();
        return {
            max: axis.toPixels(extremes.max, true),
            min: axis.toPixels(extremes.min, true),
        };
    }
    /**
     * Get the x and y alignment for the label, such that the label stays within the chart edges.
     * By default the label is right aligned horizontally, and centered vertically relative to the data point.
     *
     * @param xAxis - The Highchart axis object for the x-axis.
     * @param yAxis - The Highchart axis object for the y-axis.
     * @param point - The point object whose label we are trying to align.
     * @returns A LabelAlignment object describing how the point should be aligned relative to the data point.
     */
    static getLabelAlignment(xAxis, yAxis, point) {
        const alignment = { ...YC_DEFAULT_LABEL_ALIGNMENT };
        // If we have a label placement, then we can immediately return that without having to compute anything.
        const { labelPlacement } = point.options.custom.label;
        if (labelPlacement) {
            return YChartsScatterPlotService.locationNameToLabelAligment(labelPlacement);
        }
        const labelId = point.options.custom.label.id;
        const elem = document.getElementById(labelId);
        // If the data label doesn't exist on the DOM yet, set the label's alignment to the default value
        if (!elem)
            return alignment;
        const { offsetWidth, offsetHeight } = elem;
        const { max: xMax, min: xMin } = YChartsScatterPlotService.getExtremesPixels(xAxis);
        const { max: yMax, min: yMin } = YChartsScatterPlotService.getExtremesPixels(yAxis);
        // Get distance of point from the chart's left and right edges
        let xPixels = xAxis.toPixels(point.x, true);
        xPixels = xPixels < 0 ? 0 : xPixels;
        const rightDist = xMax - xPixels;
        const leftDist = xPixels - xMin;
        if (rightDist < offsetWidth) {
            alignment.x = 'right';
        }
        if (leftDist < offsetWidth) {
            alignment.x = 'left';
        }
        if (!point.y)
            return alignment;
        // Get distance of point from chart's top and bottom edges
        let yPixels = yAxis.toPixels(point.y, true);
        yPixels = yPixels < 0 ? 0 : yPixels;
        const topDist = yPixels - yMax;
        const bottomDist = yMin - yPixels;
        if (topDist < offsetHeight) {
            alignment.y = 'top';
        }
        if (bottomDist < offsetHeight) {
            alignment.y = 'bottom';
        }
        return alignment;
    }
    /**
     * Convert a string with axis directions into a LabelAlignment objecet
     * @param labelLocation - String that contains position on both axis
     * @returns A LabelAlignment with x and y values
     */
    static locationNameToLabelAligment(labelLocation) {
        const alignment = { ...YC_DEFAULT_LABEL_ALIGNMENT };
        if (labelLocation.includes('left')) {
            alignment.x = 'right';
        }
        else if (labelLocation.includes('right')) {
            alignment.x = 'left';
        }
        if (labelLocation.includes('top')) {
            alignment.y = 'bottom';
        }
        else if (labelLocation.includes('bottom')) {
            alignment.y = 'top';
        }
        return alignment;
    }
    /**
     * Data Label and Tooltip Formatters
     */
    /**
     * Create a function that sets an html formatted string to display as the data label.
     */
    dataLabelFormatter(scatterPlotDataLabelOptions, seriesLabelLocation) {
        const _dataLabelFormatter = function () {
            return YChartsScatterPlotService.formatter(this.point, true, scatterPlotDataLabelOptions, seriesLabelLocation);
        };
        return _dataLabelFormatter;
    }
    /**
     * Set an html formatted string to display as the tooltip.
     */
    pointFormatter() {
        return YChartsScatterPlotService.formatter(this, false);
    }
    /**
     * Convert Highcharts.Chart to Highcharts.ScatterPlotChart (an extended interface we created)
     * by adding our custom properties.
     * @param chart - A Highcharts chart
     * @returns A Highcharts chart containing our custom properties set to default values
     */
    static addCustomChartProperties(chart) {
        const _chart = chart;
        _chart.custom = { ...DEFAULT_SCATTER_PLOT_CUSTOM };
        return _chart;
    }
}
YChartsScatterPlotService.pointClickSubject = new Subject();
YChartsScatterPlotService.pointSelectSubject = new Subject();
